/**
 * The canonical catalogue.
 *
 * English is the source of truth: `MessageKey` is derived from this object, so
 * every other locale is a `Messages` record and the compiler reports a key that
 * a translation forgot or invented.
 *
 * A value is either a plain string or a set of plural forms keyed by the CLDR
 * categories `Intl.PluralRules` returns — `other` is the required fallback.
 * Placeholders are written `{name}` and filled from `t`'s second argument.
 *
 * "ElmsCare" is deliberately absent: a product name is not translated.
 */
export const en = {
    /* Verbs and nouns shared by every screen ------------------------------ */
    "common.add": "Add",
    "common.assign": "Assign",
    "common.back": "Back",
    "common.cancel": "Cancel",
    "common.clear": "Clear",
    "common.confirm": "Confirm",
    "common.continue": "Continue",
    "common.delete": "Delete",
    "common.menu": "Menu",
    "common.open": "Open",
    "common.refresh": "Refresh",
    "common.remove": "Remove",
    "common.retry": "Retry",
    "common.save": "Save",
    "common.search": "Search",
    "common.signIn": "Sign in",
    "common.signOut": "Sign out",

    /* Navigation ---------------------------------------------------------- */
    "nav.group.clinic": "Clinic",
    "nav.doctors": "Doctors",
    "nav.caretakers": "Caretakers",
    "nav.patients": "Patients",
    "nav.role": "Organization Admin",
    "sidebar.collapse": "Collapse sidebar",
    "sidebar.expand": "Expand sidebar",

    /* Language switcher --------------------------------------------------- */
    "language.label": "Language",

    /* Turned-away session ------------------------------------------------- */
    "access.denied.title": "Access denied",
    "access.denied.subtitle":
        "This console is for organization administrators. Sign in with an organization administrator account.",

    /* Command palette ----------------------------------------------------- */
    "palette.placeholder": "Jump to a section…",
    "palette.aria": "Jump to a section",
    "palette.empty": "No matches",
    "palette.section.sections": "Sections",
    "palette.section.actions": "Actions",
    "palette.foot.move": "to move",
    "palette.foot.open": "to open",

    /* Action bar ---------------------------------------------------------- */
    "crud.selected": "Selected:",
    "crud.hint.default": "Pick a row to open it",
    "crud.hint.assignPeople": "Tick the people to assign",
    "crud.hint.removePatients": "Tick the patients to remove from this list",
    "crud.hint.removeCaretakers": "Tick the caretakers to remove from this list",

    /* Table columns ------------------------------------------------------- */
    "person.column.firstName": "First Name",
    "person.column.lastName": "Last Name",
    "person.column.phone": "Phone",
    "person.column.email": "Email",

    /* Form labels. The trailing colon is stripped by antd in a horizontal
       form and re-added from CSS, so it is kept here to match the layout. */
    "person.field.firstName": "First name:",
    "person.field.lastName": "Last name:",
    "person.field.phone": "Phone number:",
    "person.field.email": "Email:",
    "person.field.password": "Password:",
    "person.field.gender": "Gender:",
    "person.field.birthday": "Birthday:",
    "person.field.country": "Country:",
    "person.field.address": "Address:",
    "person.field.address2": "Address line 2:",
    "person.field.city": "City:",
    "person.field.state": "State:",
    "person.field.postcode": "Postcode:",
    "person.field.allowedDistance": "Allowed distance:",
    "person.field.checkAllowedDistance": "Check allowed distance:",
    "person.field.doctorFirstName": "Doctor first name:",
    "person.field.doctorLastName": "Doctor last name:",
    "person.field.doctorPhone": "Doctor phone number:",
    "person.field.diagnosis": "Diagnosis:",

    /* Field examples. The structured ones (phone, email, postcode) are
       formats rather than words and read the same in every locale. */
    "person.placeholder.firstName": "John",
    "person.placeholder.lastName": "Doe",
    "person.placeholder.phone": "1 (344) 333-3333",
    "person.placeholder.email": "john.doe@email.com",
    "person.placeholder.password": "password",
    "person.placeholder.country": "Search to Select",
    "person.placeholder.address": "Road st 27",
    "person.placeholder.address2": "Left side",
    "person.placeholder.city": "New York",
    "person.placeholder.state": "NY",
    "person.placeholder.postcode": "10001",
    "person.placeholder.doctorFirstName": "Mikle",
    "person.placeholder.doctorLastName": "Santos",
    "person.placeholder.doctorPhone": "+1 456 565-3443",
    "person.placeholder.diagnosis": "Diagnosis",

    /* Field validation ---------------------------------------------------- */
    "person.rule.firstName": "please enter first name",
    "person.rule.phone": "please enter phone number",
    "person.rule.email": "please enter valid email",
    "person.rule.code": "please enter code",

    /* Enumerated values. The value sent to the API stays English; only the
       label an admin reads is translated. */
    "person.gender.male": "Male",
    "person.gender.female": "Female",
    "person.gender.other": "Other",

    "patient.hint.allowedDistance": "Metres from home before an alert is raised.",

    /* Search fields ------------------------------------------------------- */
    "list.search.person": "Search by name, phone or email",
    "list.search.people": "Search people",
    "list.search.patients": "Search patients",
    "list.search.caretakers": "Search caretakers",

    /* Empty lists --------------------------------------------------------- */
    "empty.default.title": "Nothing here yet",
    "empty.default.hint": "Records you add will show up in this list.",
    "empty.assign.title": "Nobody left to assign",
    "empty.assign.hint": "Everyone available is already on this list.",
    "empty.patients.title": "No patients assigned",
    "empty.patients.hint": "Assign patients to see them here.",
    "empty.caretakers.title": "No caretakers assigned",
    "empty.caretakers.hint": "Assign caretakers to see them here.",

    /* Record tabs --------------------------------------------------------- */
    "tabs.doctor": "Doctor",
    "tabs.caretaker": "Caretaker",
    "tabs.patients": "Patients",
    "tabs.caretakers": "Caretakers",

    /* Records ------------------------------------------------------------- */
    "doctor.new": "New doctor",
    "doctor.one": "Doctor",
    "caretaker.new": "New caretaker",
    "caretaker.one": "Caretaker",
    "patient.new": "New patient",
    "patient.one": "Patient",

    /* Assignment ---------------------------------------------------------- */
    "assign.eyebrow": "Assign",
    "assign.people.title": "Assign people",
    "assign.caretakers.title": "Assign caretakers",
    "assign.patients.title": "Assign patients",
    "assign.caretakers.forDoctor": "Caretakers not yet assigned to this doctor.",
    "assign.patients.forDoctor": "Patients not yet assigned to this doctor.",
    "assign.patients.forCaretaker": "Patients not yet assigned to this caretaker.",
    "assign.selection.count": {
        one: "{count} person",
        other: "{count} people"
    },

    /* Confirmations ------------------------------------------------------- */
    "confirm.remove.title": "Remove from clinic?",
    "confirm.remove.doctor":
        "Are you sure you want to remove doctor \"{name}\" from the clinic? Their account stays, but they leave this organization.",
    "confirm.remove.caretaker":
        "Are you sure you want to remove caretaker \"{name}\" from the clinic? Their account stays, but they leave this organization.",
    "confirm.remove.patient":
        "Are you sure you want to remove patient \"{name}\" from the clinic? Their account stays, but they leave this organization.",
    "confirm.delete.title": "Delete permanently?",
    "confirm.delete.doctor":
        "Are you sure you want to completely delete doctor \"{name}\"? This cannot be undone.",
    "confirm.delete.caretaker":
        "Are you sure you want to completely delete caretaker \"{name}\"? This cannot be undone.",
    "confirm.delete.patient":
        "Are you sure you want to completely delete patient \"{name}\"? This cannot be undone.",

    /* Failures ------------------------------------------------------------ */
    "auth.failed.title": "Authentication failed",
    "auth.failed.body": "Your session is no longer valid, please sign in again.",
    "error.server": "Server error",

    /* Account ------------------------------------------------------------- */
    "account.title": "Account",
    "account.email": "Email",
    "account.notAuthenticated": "Not authenticated",

    /* Adding a patient ---------------------------------------------------- */
    "addPatient.eyebrow": "Add patient",
    "addPatient.pageTitle": "Add patient",
    "addPatient.phone.title": "Phone number",
    "addPatient.phone.hint":
        "We send a one-time code to this number to confirm the patient owns it.",
    "addPatient.phone.label": "Phone number",
    "addPatient.phone.help": "Include the country code.",
    "addPatient.sendCode": "Send code",
    "addPatient.cannotAdd.title": "Cannot add patient",
    "addPatient.exists.own": "This patient already exists in our clinic.",
    "addPatient.exists.other": "This patient already exists in another clinic.",
    "addPatient.code.title": "Activation code",
    "addPatient.code.hint":
        "Enter the eight-character code shown in the patient's application.",
    "addPatient.code.label": "Code",
    "addPatient.code.placeholder": "XXXX-XXXX",
    "addPatient.confirm.title": "Confirm the number",
    "addPatient.confirm.hint":
        "We sent a six-digit code by SMS. Enter it to confirm the patient owns this number.",
    "addPatient.confirm.pageTitle": "Confirm phone number",
    "addPatient.otp.label": "Code",
    "addPatient.otp.help": "Six digits.",
    "addPatient.otp.placeholder": "xxx-xxx",
    "addPatient.resend": "Send again",
    /* Split in two so the countdown can be emphasised without assuming that
       the number falls at the end of the sentence in every language. */
    "addPatient.resendPrompt": "No message yet? Resend in",
    "addPatient.resendSeconds": "{seconds}s",

    /* Reference screen ---------------------------------------------------- */
    "countries.eyebrow": "Reference",
    "countries.title": "Countries",
    "countries.subtitle":
        "A development aid: the country list the phone-number formatting reads."
};
